"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/home";
import {
  getEventRegisterHref,
  getEventSlugFromPathname,
} from "../_components/event-path";
import { copy, type Locale } from "./copy";

type Tone = "session" | "meal" | "break" | "night" | "highlight";

function toneAccent(tone?: Tone) {
  switch (tone) {
    case "night":
      return "text-[#D4AF37]";
    case "highlight":
      return "text-[#C41E3A]";
    case "session":
      return "text-stone-900";
    case "meal":
    case "break":
      return "text-stone-500";
    default:
      return "text-stone-700";
  }
}

export default function ScheduleView({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const pathname = usePathname();
  const eventSlug = getEventSlugFromPathname(pathname);
  const registerHref = getEventRegisterHref(locale, eventSlug);
  const visible = useQuery(api.siteSettings.getAyacScheduleVisibility);
  const isSuperAdmin = useQuery(api.admin.isCurrentUserSuperAdmin);

  // Loading state — wait until we know the visibility status
  if (visible === undefined) {
    return (
      <div className="min-h-screen bg-[#fdf8f3]">
        <Navbar />
      </div>
    );
  }

  // Gate: hide from the public unless the toggle is on or viewer is super admin
  if (!visible && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-[#fdf8f3]">
        <Navbar />
        <section className="mx-auto flex max-w-2xl flex-col items-center px-6 pt-40 pb-24 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C41E3A]">
            {t.comingSoon}
          </div>
          <h1 className="mt-4 font-serif text-5xl leading-[0.95] tracking-tight text-stone-900 sm:text-6xl">
            Revival Summit <span className="italic text-[#C41E3A]">2026</span>
          </h1>
          <p className="mt-6 max-w-md text-stone-600">{t.comingSoonBody}</p>
          <Link
            href={registerHref}
            className="mt-10 rounded-full bg-[#C41E3A] px-6 py-3 text-sm font-medium text-white transition hover:bg-[#a8172f]"
          >
            {t.ctaRegisterHero}
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {!visible && isSuperAdmin ? (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs text-center py-2 px-4">
          Preview mode — this page is hidden from the public. Toggle in{" "}
          <Link href="/admin/settings" className="underline font-medium">
            admin settings
          </Link>{" "}
          to publish.
        </div>
      ) : null}

      <section id="schedule" className="mx-auto max-w-6xl px-6 pt-28 pb-20 sm:pt-36 sm:pb-28">
        <h1 className="font-serif text-4xl leading-tight tracking-tight text-stone-900 sm:text-5xl">
          Revival Summit <span className="italic text-[#C41E3A]">2026</span>{" "}
          <span className="text-stone-400">{t.heroTitleSub}</span>
        </h1>
        <p className="mt-3 text-sm text-stone-500">
          {t.dateRange}
          <span className="mx-2 text-stone-300">·</span>
          {t.thanksgivingNote}
        </p>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-stone-600">{t.intro}</p>

        <ol className="mt-16 space-y-16 sm:space-y-20">
          {t.days.map((day, i) => (
            <li
              key={day.date}
              className="grid gap-8 sm:grid-cols-[220px_1fr] sm:gap-14"
              style={{ animation: `fadeUp 600ms ease both ${i * 80}ms` }}
            >
              <header className="sm:sticky sm:top-24 sm:self-start">
                {day.eyebrow ? (
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#C41E3A]">
                    {day.eyebrow}
                  </div>
                ) : null}
                <div className="mt-2 font-serif text-4xl leading-none tracking-tight text-stone-900 sm:text-5xl">
                  {day.weekday}
                </div>
                <div className="mt-1 font-serif text-2xl italic text-stone-400">
                  {day.date}
                </div>
              </header>

              <div className="relative border-l border-stone-200 pl-6 sm:pl-10">
                <ul className="space-y-7">
                  {day.blocks.map((b, bi) => (
                    <li key={bi} className="relative">
                      <span
                        aria-hidden
                        className="absolute -left-[29px] top-2 h-2 w-2 rounded-full bg-stone-300 sm:-left-[45px]"
                      />
                      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                        <div className="w-36 shrink-0 font-mono text-xs uppercase tracking-wider text-stone-500">
                          {b.time}
                        </div>
                        <div className="flex-1">
                          <div
                            className={`font-serif text-2xl leading-snug ${toneAccent(
                              b.tone as Tone
                            )}`}
                          >
                            {b.label}
                            {b.wd ? (
                              <span className="ml-3 align-middle font-sans text-xs uppercase tracking-[0.18em] text-[#D4AF37]">
                                · {b.wd}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ol>

        <footer className="mt-24 border-t border-stone-200 pt-10">
          <div className="flex justify-end">
            <Link
              href={registerHref}
              className="rounded-full bg-stone-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-[#C41E3A]"
            >
              {t.ctaRegisterFooter}
            </Link>
          </div>
        </footer>
      </section>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
